import { BASE_URL } from "../types/constants";
import {
    UserLogin,
    UserRegister,
    CheckoutUserRegister,
    UserInfo,
    UserRegistrationResponse,
    ContactUsPayload,
    ResetPasswordPayload
} from "../types/user.types";
import {RestAPI} from "./rest-api.service";
import {useUserState} from "../providers/user-state.provider.ts";
import { buildMarketingAttributionHeaders } from './marketing.service';


const api = new RestAPI();

const asRecord = (value: unknown): Record<string, unknown> => (
    value && typeof value === 'object' ? value as Record<string, unknown> : {}
);

const readString = (record: Record<string, unknown>, camel: string, pascal: string): string => {
    const value = record[camel] ?? record[pascal];
    return typeof value === 'string' ? value : '';
};

/** Normalize API responses regardless of JSON naming policy. */
export const normalizeUserInfo = (value: unknown): UserInfo => {
    const record = asRecord(value);
    const rawRoles = record.roles ?? record.Roles;
    return {
        userId: readString(record, 'userId', 'UserId'),
        email: readString(record, 'email', 'Email'),
        firstName: readString(record, 'firstName', 'FirstName'),
        lastName: readString(record, 'lastName', 'LastName'),
        roles: Array.isArray(rawRoles) ? rawRoles.filter((role): role is string => typeof role === 'string') : [],
        token: readString(record, 'token', 'Token'),
        username: readString(record, 'username', 'Username') || readString(record, 'userName', 'UserName'),
        canEditUsername: (record.canEditUsername ?? record.CanEditUsername) !== false,
        usernameEditRestriction: readString(
            record,
            'usernameEditRestriction',
            'UsernameEditRestriction'
        ) || null
    };
};

const normalizeRegistrationResponse = (value: unknown): UserRegistrationResponse => {
    const record = asRecord(value);
    const userInfo = normalizeUserInfo(record);
    const accountStatusValue = readString(record, 'accountStatus', 'AccountStatus').toLowerCase();
    return {
        ...userInfo,
        accountStatus: accountStatusValue === 'existing' ? 'existing' : 'created',
        checkoutAttemptId: readString(record, 'checkoutAttemptId', 'CheckoutAttemptId') || undefined
    };
};

/**
 * Refresh profile data and issue a JWT containing the user's current role claims.
 */
export const refreshUserInfo = async (token: string): Promise<UserInfo> => {
    const url = BASE_URL + '/v1/user/refresh-session';
    const refreshedUser = normalizeUserInfo(await api.post(url, null, token));
    if (!refreshedUser.userId || !refreshedUser.token) {
        throw new Error('The refreshed user session did not include a bearer token.');
    }

    return refreshedUser;
};

/**
 * User-facing API helpers; authenticated calls pull the token from user state.
 */
export const useUserService = () => {

    const { getUserInfo } = useUserState();

    /**
     * Authenticate a user and return the login response.
     */
    const login = async (
        userLogin: UserLogin,
        options: { suppressAuthModalOnUnauthorized?: boolean } = {}
    ) => {
        const url = BASE_URL + '/v1/user/login';
        return options.suppressAuthModalOnUnauthorized === true
            ? await api.post(url, userLogin, null, {}, true)
            : await api.post(url, userLogin);
    };

    /**
     * Register a new user, optionally passing a marketing event id for server/browser conversion dedupe.
     */
    const register = async (userRegister: UserRegister, marketingEventId?: string) => {
        const url = BASE_URL + '/v1/user/register';
        const marketingHeaders = buildMarketingAttributionHeaders(marketingEventId);
        return Object.keys(marketingHeaders).length > 0
            ? await api.post(url, userRegister, null, marketingHeaders)
            : await api.post(url, userRegister);
    };

    /**
     * Create or securely recover an account during subscription checkout.
     * The backend returns an immediately usable bearer token so payment can continue in one submission.
     */
    const registerForCheckout = async (
        userRegister: CheckoutUserRegister,
        marketingEventId?: string
    ): Promise<UserRegistrationResponse> => {
        const url = BASE_URL + '/v1/user/register';
        const marketingHeaders = buildMarketingAttributionHeaders(marketingEventId);
        const response = Object.keys(marketingHeaders).length > 0
            ? await api.post(url, userRegister, null, marketingHeaders)
            : await api.post(url, userRegister);
        const normalized = normalizeRegistrationResponse(response);
        if (!normalized.userId || !normalized.token || !normalized.username || !normalized.email) {
            throw new Error('Your account was created, but the sign-in session could not be started. Please sign in to continue.');
        }
        return normalized;
    };

    /**
     * Log out the current user.
     */
    const logout = async () => {
        const url = BASE_URL + '/v1/user/logout';
        return await api.get(url);
    };

    /**
     * Update the current user's profile.
     */
    const updateUser = async (userProfile: UserInfo): Promise<UserInfo> => {
        const userInfo = getUserInfo();
        const url = BASE_URL + '/v1/user/update';
        const updatedUser = normalizeUserInfo(await api.put<UserInfo>(url, userProfile, userInfo?.token));
        if (!updatedUser.userId || !updatedUser.token) {
            throw new Error('The updated profile did not include a refreshed sign-in session.');
        }
        return updatedUser;
    };

    /**
     * Refresh user profile details and role claims using a token.
     */
    const refreshUser = async (token: string | undefined) =>  {
        if (!token) {
            throw new Error('A bearer token is required to refresh user information.');
        }

        return await refreshUserInfo(token);
    };

    /**
     * Change the current user's password.
     */
    const changePassword = async (CurrentPassword: string, NewPassword: string) => {
        const userInfo = getUserInfo();
        const url = BASE_URL + '/v1/user/change-password';
        const payload = { CurrentPassword, NewPassword };
        return await api.post(url, payload, userInfo?.token);
    };

    /**
     * Deactivate the current user's account once subscription cancellation is scheduled.
     */
    const deactivateAccount = async () => {
        const userInfo = getUserInfo();
        const url = BASE_URL + '/v1/user/cancel-membership';
        return await api.postVoid(url, null, userInfo?.token);
    };

    /**
     * Submit a contact-us request.
     */
    const contactUs = async (payload: ContactUsPayload) => {
        const url = BASE_URL + '/v1/site/contact-us';
        return await api.post(url, payload);
    };

    /**
     * Fetch site information.
     */
    const siteInfo = async () =>  {
        const url = BASE_URL + '/v1/site/site-info';
        return await api.get(url);
    };

    /**
     * Request a password reset email.
     */
    const forgotPassword = async (email: string) =>  {
        const url = BASE_URL + '/v1/user/forgot-password';
        return await api.post(url, email);
    };

    /**
     * Reset password with a token payload.
     */
    const resetPassword = async (payload: ResetPasswordPayload) =>  {
        const url = BASE_URL + '/v1/user/reset-password';
        return await api.post(url, payload);
    };

    return {
        login,
        register,
        registerForCheckout,
        logout,
        updateUser,
        refreshUser,
        changePassword,
        deactivateAccount,
        contactUs,
        siteInfo,
        forgotPassword,
        resetPassword
    };
};
